import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ShoppingBag, Truck, Tag, MapPin, Timer } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { QuantityStepper } from "../components/QuantityStepper";
import { ProductCard } from "../components/ProductCard";
import { LiveStatusDot } from "../components/LiveStatusDot";
import { ReserveModal } from "../components/ReserveModal";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [variantIdx, setVariantIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [reserveFor, setReserveFor] = useState(null);
  const cart = useCart();
  const wishlist = useWishlist();

  useEffect(() => {
    setProduct(null);
    setError(null);
    setActiveImg(0);
    setVariantIdx(0);
    setQty(1);
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch((e) =>
        setError(e?.response?.status === 404 ? "Product not found." : "Could not load product.")
      );
  }, [slug]);

  useEffect(() => {
    if (!product?.category_slug) return;
    api
      .get(`/products?category=${product.category_slug}&limit=4`)
      .then((r) => setRelated((r.data.items || []).filter((p) => p.slug !== product.slug).slice(0, 4)));
  }, [product?.category_slug, product?.slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="product-error">
        <p className="label-overline text-jow-gold">Oops</p>
        <h1 className="font-display text-4xl md:text-5xl mt-3 text-jow-navy">{error}</h1>
        <Link to="/shop" className="btn-secondary-jow mt-8 inline-flex">
          Back to atelier
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 grid md:grid-cols-2 gap-10 animate-pulse" data-testid="product-loading">
        <div className="aspect-square bg-white border border-jow-navy/10" />
        <div className="space-y-4">
          <div className="h-6 bg-white border border-jow-navy/10 w-1/2" />
          <div className="h-12 bg-white border border-jow-navy/10 w-3/4" />
          <div className="h-24 bg-white border border-jow-navy/10" />
        </div>
      </div>
    );
  }

  const variant = product.variants[variantIdx];
  const variantStock =
    (variant?.stock_warehouse || 0) +
    Object.values(variant?.stock_per_rikshaw || {}).reduce((a, b) => a + (b || 0), 0);
  const wishlisted = wishlist.isWishlisted(product.id);

  const handleAdd = async () => {
    if (variantStock <= 0) return;
    await cart.addItem(product.id, variant.sku, qty, product.name);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid={`product-${product.slug}`}>
      <Link to="/shop" className="label-overline text-jow-pink hover:underline">
        ← Back to atelier
      </Link>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Image gallery */}
        <div className="lg:col-span-7" data-testid="product-gallery">
          <motion.div
            key={activeImg}
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="aspect-[4/5] overflow-hidden border border-jow-gold/40 bg-white"
          >
            <img
              src={product.images[activeImg]}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-main-image"
            />
          </motion.div>
          <div className="mt-4 grid grid-cols-4 gap-3">
            {product.images.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveImg(i)}
                className={`aspect-square overflow-hidden border ${
                  i === activeImg ? "border-jow-pink" : "border-jow-navy/10"
                }`}
                data-testid={`product-thumb-${i}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-5 space-y-6">
          <div>
            <span className="label-overline text-jow-gold capitalize">
              {product.category_slug?.replace("-", " ")}
            </span>
            <h1 className="font-display text-4xl md:text-5xl mt-2 text-jow-navy leading-tight" data-testid="product-name">
              {product.name}
            </h1>
          </div>

          <div className="flex items-baseline gap-4">
            <span className="font-display text-3xl text-jow-navy" data-testid="product-price">
              ₹{product.price_inr.toLocaleString("en-IN")}
            </span>
            {product.mrp_inr > product.price_inr && (
              <>
                <span className="text-base text-jow-navy/45 line-through">
                  ₹{product.mrp_inr.toLocaleString("en-IN")}
                </span>
                <span className="text-xs text-jow-pink uppercase tracking-widest">
                  -{product.discount_pct}% off
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-3 text-sm text-jow-navy/70">
            <Tag size={14} className="text-jow-gold" />
            <span>{product.material}</span>
          </div>

          <p className="text-jow-navy/75 leading-relaxed border-t border-jow-navy/10 pt-6">
            {product.description}
          </p>

          {/* Variant selector */}
          {product.variants.length > 1 && (
            <div data-testid="product-variants">
              <p className="label-overline text-jow-navy/70 mb-2">Variant</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v, i) => (
                  <button
                    key={v.sku}
                    type="button"
                    onClick={() => setVariantIdx(i)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border ${
                      i === variantIdx
                        ? "bg-jow-navy text-jow-cream border-jow-navy"
                        : "bg-white text-jow-navy border-jow-navy/20"
                    }`}
                    data-testid={`variant-${v.sku}`}
                  >
                    {v.attrs?.color}
                    {v.attrs?.size && v.attrs.size !== "Free Size" ? ` · ${v.attrs.size}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4">
            <div data-testid="product-qty">
              <QuantityStepper
                value={qty}
                min={1}
                max={Math.max(1, variantStock)}
                onChange={setQty}
                testid="qty"
              />
            </div>
            <span className="text-xs text-jow-navy/55">
              {variantStock > 0 ? `${variantStock} in stock` : "Out of stock"}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={handleAdd}
              disabled={variantStock <= 0}
              className="btn-primary-jow flex-1"
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag size={14} /> Add to Cart
            </button>
            <button
              type="button"
              onClick={() =>
                wishlisted ? wishlist.remove(product.id) : wishlist.add(product.id, product.name)
              }
              className={`btn-secondary-jow ${wishlisted ? "bg-jow-pink/10 text-jow-pink border-jow-pink" : ""}`}
              data-testid="add-to-wishlist-btn"
            >
              <Heart
                size={14}
                fill={wishlisted ? "#E91E63" : "transparent"}
                color={wishlisted ? "#E91E63" : "currentColor"}
              />
              {wishlisted ? "Saved" : "Add to Wishlist"}
            </button>
          </div>

          {/* Available on Rikshaw widget */}
          {product.available_rikshaws?.length > 0 && (
            <div
              className="border border-jow-gold/40 bg-jow-cream p-5 mt-4"
              data-testid="rikshaw-availability"
            >
              <p className="label-overline text-jow-gold flex items-center gap-2 mb-3">
                <Truck size={13} /> Available now on a rikshaw
              </p>
              <ul className="space-y-2">
                {product.available_rikshaws.map((r) => {
                  const stockOnR = (variant?.stock_per_rikshaw || {})[r.id] || 0;
                  const lastPingMs = r.last_ping_at ? Date.now() - new Date(r.last_ping_at).getTime() : Infinity;
                  const canReserve = stockOnR > 0 && lastPingMs < 30 * 60 * 1000;
                  return (
                    <li
                      key={r.id}
                      className="flex items-center justify-between gap-2 text-sm"
                      data-testid={`rik-row-${r.code}`}
                    >
                      <span className="flex items-center gap-2 text-jow-navy min-w-0">
                        <MapPin size={13} className="text-jow-gold shrink-0" />
                        <strong>{r.code}</strong>
                        <span className="text-jow-navy/55 truncate">· {r.area || r.city}</span>
                        <LiveStatusDot lastPingAt={r.last_ping_at} testid={`pdp-dot-${r.code}`} />
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link
                          to={`/find-us?focus=${r.id}`}
                          className="text-xs label-overline text-jow-pink hover:underline"
                          data-testid={`rik-locate-${r.code}`}
                        >
                          Map →
                        </Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {(() => {
                const bestR = product.available_rikshaws.find((r) => {
                  const stockOnR = (variant?.stock_per_rikshaw || {})[r.id] || 0;
                  const lastPingMs = r.last_ping_at ? Date.now() - new Date(r.last_ping_at).getTime() : Infinity;
                  return stockOnR > 0 && lastPingMs < 30 * 60 * 1000;
                });
                return bestR ? (
                  <button
                    type="button"
                    onClick={() => setReserveFor(bestR)}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-jow-pink text-white text-[11px] uppercase tracking-[0.18em] py-3 hover:brightness-110"
                    data-testid="reserve-cta"
                  >
                    <Timer size={13} /> Reserve on {bestR.code} — Try &amp; Buy
                  </button>
                ) : null;
              })()}
            </div>
          )}

          {product.occasion_tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-4">
              {product.occasion_tags.map((t) => (
                <span
                  key={t}
                  className="text-[10px] uppercase tracking-widest border border-jow-navy/15 px-3 py-1 text-jow-navy/70"
                  data-testid={`occ-tag-${t}`}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-24" data-testid="product-related">
          <h2 className="font-display text-3xl text-jow-navy mb-8">You may also love</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <ReserveModal
        open={!!reserveFor}
        onClose={() => setReserveFor(null)}
        product={product}
        variant={variant}
        rikshaw={reserveFor}
        onReserved={() => setReserveFor(null)}
      />
    </div>
  );
}
